import React, { useState, useEffect, useRef } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";
import { toLocalDateString } from "src/utils/formatUtils";
import {
  SUBMISSION_METHODS,
  PRODUCT_TYPES,
  getMethodLabel,
  getProductName,
  getStatusInfo,
  formatTimestamp,
} from "./constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor } from "@progress/kendo-data-query";
import ReactDOM from "react-dom";
import {
  IReassignedSession,
  IReassignedSessionsResponse,
  IReassignedSessionFilters,
} from "src/interfaces/Reports/IReassignedSession";
import ReassignedSessionDetail from "./ReassignedSessionDetail";

// Matches the server-side cap in ReassignedSessionsController.ExportCsv.
const MAX_DOWNLOAD_RECORDS = 50_000;

// --- Loading Panel ---

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

// --- Component ---

const ReassignedSessions: React.FC = () => {
  // Grid state
  const [sessions, setSessions] = useState<IReassignedSession[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ skip: 0, take: 25 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "addedOn", dir: "desc" },
  ]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Filter state (draft = form inputs, applied = used for fetching)
  const emptyFilters: IReassignedSessionFilters = {
    dateFrom: null,
    dateTo: null,
    receivedBy: "",
    reassignedProductId: null,
  };
  const [filters, setFilters] =
    useState<IReassignedSessionFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<IReassignedSessionFilters>(emptyFilters);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSession, setSelectedSession] =
    useState<IReassignedSession | null>(null);

  // Track latest request so stale responses are ignored
  const requestIdRef = useRef(0);

  // --- Data Loading ---

  // --- Effects ---

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const currentSort = sort.length > 0 ? sort[0] : null;

        const params = new URLSearchParams();
        params.append("skip", String(page.skip));
        params.append("take", String(page.take));
        params.append("sortField", currentSort?.field || "addedOn");
        params.append("sortDir", currentSort?.dir || "desc");

        if (appliedFilters.dateFrom) {
          params.append("dateFrom", toLocalDateString(appliedFilters.dateFrom));
        }
        if (appliedFilters.dateTo) {
          // Send end-of-day so the filter includes the entire selected date
          params.append(
            "dateTo",
            toLocalDateString(appliedFilters.dateTo) + "T23:59:59",
          );
        }
        if (appliedFilters.receivedBy) {
          params.append("receivedBy", appliedFilters.receivedBy);
        }
        if (appliedFilters.reassignedProductId !== null) {
          params.append(
            "reassignedProductId",
            String(appliedFilters.reassignedProductId),
          );
        }

        const response: IReassignedSessionsResponse = await callApi(
          `reports/reassigned-sessions?${params.toString()}`,
          HttpMethod.GET,
        );

        if (requestId !== requestIdRef.current) return;

        setSessions(response.data || []);
        setTotal(response.total || 0);
      } catch (err: any) {
        if (requestId !== requestIdRef.current) return;
        console.error("Error fetching reassigned sessions:", err);
        setError(`Error fetching sessions: ${err.message || err}`);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [page, sort, appliedFilters]);

  // --- Handlers ---

  const handleSearch = () => {
    setPage({ skip: 0, take: page.take });
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setPage({ skip: 0, take: 25 });
    setAppliedFilters({ ...emptyFilters });
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleRowClick = (event: GridRowClickEvent) => {
    setSelectedSession(event.dataItem as IReassignedSession);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSession(null);
  };

  // --- Custom Cells ---

  const DateCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!dataItem || !field) return <td>-</td>;
    return <td>{formatTimestamp(dataItem[field])}</td>;
  };

  const StatusCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const statusInfo = getStatusInfo(dataItem.statusId);
    return (
      <td>
        <span className={`badge ${statusInfo.badge}`}>{statusInfo.name}</span>
      </td>
    );
  };

  const MethodCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    return <td>{getMethodLabel(dataItem.receivedBy)}</td>;
  };

  const ProductCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    return <td>{getProductName(dataItem.reassignedProductId)}</td>;
  };

  const FileNameCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    if (dataItem.receivedBy === "a") {
      return <td className="text-muted fst-italic">-- API Request --</td>;
    }
    return <td>{dataItem.fileName || ""}</td>;
  };

  const NumberCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!dataItem || !field) return <td>-</td>;
    const value = dataItem[field];
    return <td>{value?.toLocaleString() || "0"}</td>;
  };

  // --- CSV Download ---

  const handleDownloadCsv = () => {
    if (total === 0) return;
    if (total > MAX_DOWNLOAD_RECORDS) {
      setError(
        `The download exceeds ${MAX_DOWNLOAD_RECORDS.toLocaleString()} records ` +
          `(${total.toLocaleString()} total). Please narrow your date range.`,
      );
      return;
    }
    setError("");

    // Same filters and sort the grid is showing; the server builds the CSV in one query.
    const currentSort = sort.length > 0 ? sort[0] : null;
    const params = new URLSearchParams();
    params.append("sortField", currentSort?.field || "addedOn");
    params.append("sortDir", currentSort?.dir || "desc");

    // toLocalDateString is typed string | null and returns null only for a falsy date,
    // which these branches have already ruled out.
    if (appliedFilters.dateFrom) {
      params.append("dateFrom", toLocalDateString(appliedFilters.dateFrom)!);
    }
    if (appliedFilters.dateTo) {
      params.append(
        "dateTo",
        toLocalDateString(appliedFilters.dateTo)! + "T23:59:59",
      );
    }
    if (appliedFilters.receivedBy) {
      params.append("receivedBy", appliedFilters.receivedBy);
    }
    if (appliedFilters.reassignedProductId !== null) {
      params.append(
        "reassignedProductId",
        String(appliedFilters.reassignedProductId),
      );
    }

    window.open(
      `${runtimeConfig.apiUrl}/reports/reassigned-sessions/export-csv?${params.toString()}`,
      "_blank",
    );
  };

  // --- Render ---

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Reassigned Sessions</h4>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleDownloadCsv}
          disabled={total === 0}
        >
          <i className="bi bi-download me-1"></i>Download CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-2">
              <label className="form-label mb-1">Date From</label>
              <DatePicker
                selected={filters.dateFrom}
                onChange={(date) => setFilters({ ...filters, dateFrom: date })}
                dateFormat="yyyy-MM-dd"
                isClearable
                className="form-control"
                placeholderText="Start date"
                wrapperClassName="d-block"
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label mb-1">Date To</label>
              <DatePicker
                selected={filters.dateTo}
                onChange={(date) => setFilters({ ...filters, dateTo: date })}
                dateFormat="yyyy-MM-dd"
                isClearable
                className="form-control"
                placeholderText="End date"
                wrapperClassName="d-block"
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label mb-1">Method</label>
              <select
                className="form-select"
                value={filters.receivedBy}
                onChange={(e) =>
                  setFilters({ ...filters, receivedBy: e.target.value })
                }
              >
                <option value="">All</option>
                {Object.entries(SUBMISSION_METHODS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label mb-1">Product</label>
              <select
                className="form-select"
                value={filters.reassignedProductId ?? ""}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    reassignedProductId: e.target.value
                      ? parseInt(e.target.value)
                      : null,
                  })
                }
              >
                <option value="">All</option>
                {Object.entries(PRODUCT_TYPES).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <button className="btn btn-primary me-2" onClick={handleSearch}>
                Search
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Grid */}
      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={sessions}
          total={total}
          pageable={{ buttonCount: 5, pageSizes: [25, 50, 100] }}
          skip={page.skip}
          take={page.take}
          onPageChange={handlePageChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          resizable={true}
          onRowClick={handleRowClick}
          style={{ height: "calc(100vh - 295px)", cursor: "pointer" }}
        >
          <Column field="sessionId" title="Session ID" width={120} />
          <Column
            field="receivedBy"
            title="Method"
            width={90}
            cell={MethodCell}
            sortable={false}
          />
          <Column field="addedOn" title="Date" width={160} cell={DateCell} />
          <Column
            field="fileName"
            title="File Name"
            minResizableWidth={150}
            cell={FileNameCell}
          />
          <Column
            field="totalRecords"
            title="Total Records"
            width={120}
            cell={NumberCell}
          />
          <Column
            field="totalIsReassigned"
            title="Total Reassigned"
            width={150}
            cell={NumberCell}
          />
          <Column
            field="statusId"
            title="Status"
            width={130}
            cell={StatusCell}
            sortable={false}
          />
          <Column
            field="reassignedProductId"
            title="Product"
            width={200}
            cell={ProductCell}
            sortable={false}
          />
          <Column field="ipAddress" title="IP Address" width={140} />
          <Column
            field="campaignName"
            title="Campaign"
            minResizableWidth={120}
            cell={(props: GridCellProps) => (
              <td>{props.dataItem?.campaignName || "N/A"}</td>
            )}
          />
        </Grid>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedSession && (
        <ReassignedSessionDetail
          session={selectedSession}
          onClose={handleCloseDetailModal}
        />
      )}
    </div>
  );
};

export default ReassignedSessions;
